import { create } from "zustand";
import type { ProductDetailCoupon } from "@/shared/types/product.types";

/**
 * Logged-in Apply with the line not yet in the cart arms the coupon and nudges
 * Add to Cart / Buy Now. Logged-out Apply skips this store and goes to guest checkout.
 *
 * It lives in a store, like `productCarStore`, because the coupon strip and those two
 * buttons are siblings under the product view with no prop path between them.
 *
 * `nudgeCount` is a counter rather than a flag: tapping Apply again has to shake again,
 * and a boolean that is already `true` would fire nothing.
 *
 * `armedCoupon` is the tap intent. It stays until Add to Cart / Buy Now consumes it, or
 * the product page leaves, so a later plain add on another page cannot inherit it.
 *
 * The store outlives any one product page, so a count left standing would replay the last
 * nudge on the next product opened. Whoever shakes calls `consume()`, which puts the
 * count back to 0 and leaves nothing for a later page to pick up.
 */
export interface ArmedProductCoupon {
  productId: string;
  slug: string;
  coupon: ProductDetailCoupon;
}

interface ProductCouponNudgeState {
  nudgeCount: number;
  armedCoupon: ArmedProductCoupon | null;
  arm: (coupon: ArmedProductCoupon) => void;
  consume: () => void;
  takeArmed: (productId: string) => ArmedProductCoupon | null;
  clearArmed: () => void;
}

export const useProductCouponNudgeStore = create<ProductCouponNudgeState>((set, get) => ({
  nudgeCount: 0,
  armedCoupon: null,
  arm: (coupon) =>
    set((state) => ({
      nudgeCount: state.nudgeCount + 1,
      armedCoupon: coupon,
    })),
  consume: () => set({ nudgeCount: 0 }),
  takeArmed: (productId) => {
    const current = get().armedCoupon;
    if (!current || current.productId !== productId) return null;
    set({ armedCoupon: null });
    return current;
  },
  clearArmed: () => set({ armedCoupon: null }),
}));
